/**
 * Admin Coins Console (Phase 2: Coins)
 *
 * - Rate editor: shown only to SUPER_ADMIN. Reads the current sarPerCoin from
 *   public settings and saves a new value via adminService.setCoinRate.
 * - Grant / deduct: credit or debit a user's balance, then shows that user's
 *   balance + recent ledger via adminService.getUserCoins.
 *
 * Both write endpoints are re-auth-gated; the apiClient injects the
 * X-Reauth-Token and shows the ReauthModal transparently — no custom handling.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ArrowLeft, Coins, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import Button from '../../../../components/base/Button';
import { useAuth } from '../../../../hooks/useAuth';
import { useToast } from '../../../../components/common/ToastProvider';
import adminService, { type CoinTransaction } from '../../../../services/adminService';
import { getCoinRate } from '../../../../services/settingsService';

type Direction = 'credit' | 'debit';

// ============================================================================
// RATE EDITOR (SUPER_ADMIN only)
// ============================================================================

function RateEditor() {
  const { t } = useTranslation('coins');
  const { showToast } = useToast();

  const [currentRate, setCurrentRate] = useState<number | null>(null);
  const [rateInput, setRateInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const loadRate = useCallback(async () => {
    setIsLoading(true);
    const rate = await getCoinRate();
    setCurrentRate(rate);
    setRateInput(String(rate));
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadRate();
  }, [loadRate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = Number(rateInput);
    if (!Number.isFinite(value) || value <= 0) {
      showToast(t('admin.rateInvalid'), 'error');
      return;
    }
    try {
      setIsSaving(true);
      await adminService.setCoinRate({ sarPerCoin: value });
      setCurrentRate(value);
      showToast(t('admin.rateSaved'), 'success');
    } catch (err) {
      console.error('Failed to update coin rate:', err);
      showToast(t('admin.rateError'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] dark:text-white mb-1">
        {t('admin.rateTitle')}
      </h2>
      <p className="text-sm text-gray-500 mb-5">{t('admin.rateSubtitle')}</p>

      {isLoading ? (
        <Loader2 className="w-5 h-5 animate-spin text-[#004D40]" />
      ) : (
        <>
          <div className="mb-4 inline-flex items-center gap-2 rounded-lg bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 px-3 py-2 text-sm text-[#003930] dark:text-[#B7DDD7]">
            <span>{t('admin.currentRate')}:</span>
            <span className="font-semibold">
              {t('admin.rateValue', { rate: (currentRate ?? 0).toLocaleString() })}
            </span>
          </div>

          <form onSubmit={handleSave} className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.rateLabel')}</label>
              <input
                type="number"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                className="w-48 px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                min="0"
                step="0.01"
                required
              />
              <p className="text-xs text-gray-500 mt-1">{t('admin.rateHint')}</p>
            </div>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? t('admin.submitting') : t('admin.saveRate')}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}

// ============================================================================
// GRANT / DEDUCT + USER LEDGER
// ============================================================================

function GrantDeductPanel() {
  const { t, i18n } = useTranslation('coins');
  const isRTL = i18n.language === 'ar';
  const { showToast } = useToast();

  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState<Direction>('credit');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Loaded user ledger
  const [ledgerUserId, setLedgerUserId] = useState<string | null>(null);
  const [ledgerBalance, setLedgerBalance] = useState<number | null>(null);
  const [ledger, setLedger] = useState<CoinTransaction[]>([]);
  const [isLoadingLedger, setIsLoadingLedger] = useState(false);

  const loadLedger = useCallback(
    async (id: string) => {
      try {
        setIsLoadingLedger(true);
        const data = await adminService.getUserCoins(id);
        setLedgerUserId(id);
        setLedgerBalance(data.balance);
        setLedger(data.transactions);
      } catch (err) {
        console.error('Failed to load user coins:', err);
        showToast(t('admin.loadUserError'), 'error');
      } finally {
        setIsLoadingLedger(false);
      }
    },
    [showToast, t],
  );

  const handleLookup = () => {
    const id = userId.trim();
    if (!id) {
      showToast(t('admin.userIdRequired'), 'error');
      return;
    }
    loadLedger(id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = userId.trim();
    const amountValue = Number(amount);
    if (!id) {
      showToast(t('admin.userIdRequired'), 'error');
      return;
    }
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      showToast(t('admin.amountInvalid'), 'error');
      return;
    }
    if (!reason.trim()) {
      showToast(t('admin.reasonRequired'), 'error');
      return;
    }
    try {
      setIsSubmitting(true);
      const result = await adminService.grantUserCoins(id, {
        amount: amountValue,
        direction,
        reason: reason.trim(),
      });
      showToast(t('admin.grantSuccess', { balance: result.balance.toLocaleString() }), 'success');
      setAmount('');
      setReason('');
      // Refresh the ledger for this user.
      await loadLedger(id);
    } catch (err) {
      console.error('Failed to grant/deduct coins:', err);
      showToast(t('admin.grantError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white';

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(isRTL ? 'ar-SA' : 'en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] dark:text-white mb-1">
        {t('admin.grantTitle')}
      </h2>
      <p className="text-sm text-gray-500 mb-5">{t('admin.grantSubtitle')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* User ID */}
        <div>
          <label className="block text-sm font-medium mb-2">{t('admin.fieldUserId')}</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className={`${inputClass} font-mono`}
              required
            />
            <Button type="button" variant="outline" onClick={handleLookup}>
              <Search className="w-4 h-4 mr-2" />
              {t('admin.loadUser')}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">{t('admin.fieldUserIdHint')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Amount */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.fieldAmount')}</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={inputClass}
              min="1"
              step="1"
              required
            />
          </div>
          {/* Direction */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.fieldDirection')}</label>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value as Direction)}
              className={inputClass}
            >
              <option value="credit">{t('admin.directionCredit')}</option>
              <option value="debit">{t('admin.directionDebit')}</option>
            </select>
          </div>
        </div>

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium mb-2">{t('admin.fieldReason')}</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={inputClass}
            required
          />
          <p className="text-xs text-gray-500 mt-1">{t('admin.fieldReasonHint')}</p>
        </div>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t('admin.submitting') : t('admin.submit')}
        </Button>
      </form>

      {/* User ledger */}
      {ledgerUserId && (
        <div className="mt-8 pt-6 border-t dark:border-gray-700">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('admin.ledgerTitle')}
            </h3>
            <div className="inline-flex items-center gap-2 rounded-lg bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 px-3 py-1.5 text-sm text-[#003930] dark:text-[#B7DDD7]">
              <span>{t('admin.ledgerBalance')}:</span>
              <span className="font-semibold">{(ledgerBalance ?? 0).toLocaleString()}</span>
            </div>
          </div>

          {isLoadingLedger ? (
            <Loader2 className="w-5 h-5 animate-spin text-[#004D40]" />
          ) : (
            <div className="overflow-x-auto rounded-lg border dark:border-gray-700">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-start text-xs font-medium text-gray-700 dark:text-gray-300">
                      {t('colDate')}
                    </th>
                    <th className="px-4 py-2 text-start text-xs font-medium text-gray-700 dark:text-gray-300">
                      {t('colType')}
                    </th>
                    <th className="px-4 py-2 text-start text-xs font-medium text-gray-700 dark:text-gray-300">
                      {t('colReason')}
                    </th>
                    <th className="px-4 py-2 text-end text-xs font-medium text-gray-700 dark:text-gray-300">
                      {t('colAmount')}
                    </th>
                    <th className="px-4 py-2 text-end text-xs font-medium text-gray-700 dark:text-gray-300">
                      {t('colBalance')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-gray-700">
                  {ledger.map((txn) => {
                    const isCredit = txn.amount >= 0;
                    return (
                      <tr key={txn.id}>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {formatDate(txn.createdAt)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                          {t(`type.${txn.type}`)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">{txn.reason || '-'}</td>
                        <td
                          className={`px-4 py-2 text-end text-sm font-semibold whitespace-nowrap ${
                            isCredit
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {isCredit ? '＋' : '－'}
                          {Math.abs(txn.amount).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-end text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {txn.balanceAfter.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                  {ledger.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-gray-500 text-sm">
                        {t('admin.ledgerEmpty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function AdminCoinsPage() {
  const { t, i18n } = useTranslation('coins');
  const isRTL = i18n.language === 'ar';
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin" className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 rounded-xl flex items-center justify-center">
              <Coins className="w-5 h-5 text-[#004D40]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white">
                {t('admin.title')}
              </h1>
              <p className="text-gray-500">{t('admin.subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {isSuperAdmin && <RateEditor />}
          <GrantDeductPanel />
        </div>
      </div>
    </div>
  );
}
